import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Table, Input, Spinner } from 'reactstrap';
import { formatPrice } from 'lib/utils/formatters';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getEarnestDepositGlobalValues, updateEarnestDepositPaymentById } from 'endpoints/builder/builderPropertyListingDetails';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

interface FormValues {
    inspection_report: number;
    rentalAgreementPercentage?: number;
    deed_recording_percentage?: number;
    tax_percentage: number;
    identity_verification_percentage: number;
    transport_logistics_percentage: number;
    platform_fee_percentage: number;
}

interface FinalDisclosureModalProps {
    isOpen: boolean;
    toggle: () => void;
    earnestDepositData: any;
    onUpdate?: (updatedData: any) => void;
}

// Validation schema
const validationSchema = Yup.object({
    inspection_report: Yup.number().min(0, 'Must be non-negative').required('Required'),
    deed_recording_percentage: Yup.number().min(0, 'Must be non-negative'),
    rentalAgreementPercentage: Yup.number().min(0, 'Must be non-negative'),
    tax_percentage: Yup.number().min(0, 'Must be non-negative').required('Required'),
    identity_verification_percentage: Yup.number().min(0, 'Must be non-negative').required('Required'),
    transport_logistics_percentage: Yup.number().min(0, 'Must be non-negative').required('Required'),
    platform_fee_percentage: Yup.number().min(0, 'Must be non-negative').required('Required'),
});

const FinalDisclosureModal: React.FC<FinalDisclosureModalProps> = ({
    isOpen,
    toggle,
    earnestDepositData,
    onUpdate
}) => {
    // Determine type based on agreement for fetching global earnest deposit values
    const earnestType = earnestDepositData?.agreement?.agreement_type === 'rental_agreement' ? 'rental' : 'sell';

    // API Query to fetch global earnest deposit values for API column
    const { data: globalValues, isLoading: isLoadingGlobal } = useQuery({
        queryKey: ['earnestDepositGlobalValues', earnestType],
        queryFn: () => getEarnestDepositGlobalValues(earnestType),
        enabled: isOpen,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnReconnect: true,
        refetchOnWindowFocus: false,
        retry: 3,
    });

    // Update mutation
    const updateMutation = useMutation({
        mutationFn: updateEarnestDepositPaymentById,
        onSuccess: (response) => {
            toast.success('Final disclosure updated successfully');
            if (onUpdate && response?.data) {
                onUpdate(response.data);
            }
            // toggle();
        },
        onError: (error) => {
            console.error('Update failed:', error);
        }
    });

    // Get initial values from earnestDepositData
    const getInitialValues = (): FormValues => ({
        inspection_report: earnestDepositData?.inspection_report || 0,
        deed_recording_percentage: earnestDepositData?.deed_recording_percentage || 0,
        rentalAgreementPercentage: earnestDepositData?.rental_agreement || 0,
        tax_percentage: earnestDepositData?.tax_percentage || 0,
        identity_verification_percentage: earnestDepositData?.identity_verification_percentage || 0,
        transport_logistics_percentage: earnestDepositData?.transport_logistics_percentage || 0,
        platform_fee_percentage: earnestDepositData?.platform_fee_percentage || 0,
    });

    // Formik form
    const formik = useFormik({
        initialValues: getInitialValues(),
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const isRental = earnestDepositData?.agreement?.agreement_type === 'rental_agreement';
            
            // Create base payload without the conditional fields
            const { deed_recording_percentage, rentalAgreementPercentage, ...baseValues } = values;
            
            const submitData: any = {
                ...baseValues,
                earnestPaymentId: earnestDepositData?.id || ''
            };
            
            // Only include the active field based on agreement type
            if (isRental) {
                submitData.rentalAgreementPercentage = rentalAgreementPercentage || 0;
            } else {
                submitData.deed_recording_percentage = deed_recording_percentage || 0;
            }
            
            updateMutation.mutate(submitData);
        },
    });

    // Reset form to initial values (Clear button)
    const handleClear = () => {
        formik.resetForm({
            values: getInitialValues()
        });
    };

    // Check if form values have changed from initial values
    const hasFormChanged = () => {
        const initialValues = getInitialValues();
        return Object.keys(initialValues).some(
            key => formik.values[key as keyof FormValues] !== initialValues[key as keyof FormValues]
        );
    };

    const globalEarnestValues = globalValues?.data || {};

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>
                <h5 className="modal-title mb-0">Final Disclosure</h5>
            </ModalHeader>
            <ModalBody>
                {/* Loading State */}
                {isLoadingGlobal && (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading disclosure data...</p>
                    </div>
                )}

                {/* Content */}
                {!isLoadingGlobal && (
                    <>
                        <div className="mb-3">
                            <p className="text-muted mb-4">
                                Update the final disclosure amounts below. Set value to 0 if you don't want to include any amount.
                            </p>
                        </div>

                        <form onSubmit={formik.handleSubmit}>
                            <Table responsive borderless className="table-borderless">
                                <thead className="table-light">
                                    <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                        <th style={{ width: '35%', fontWeight: 600, color: '#495057', verticalAlign: 'middle', borderRight: 'none' }}>Description</th>
                                        <th style={{ width: '30%', fontWeight: 600, color: '#495057', verticalAlign: 'middle', borderRight: 'none' }} className="text-center">Default Value</th>
                                        <th style={{ width: '35%', fontWeight: 600, color: '#495057', verticalAlign: 'middle', borderRight: 'none' }} className="text-center">Expenses</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Inspection Report */}
                                    <tr style={{ border: 'none' }}>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#495057' }}>Inspection Report</span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                ${formatPrice(globalEarnestValues?.inspection_report || 0)}
                                            </span>
                                        </td>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <Input
                                                type="number"
                                                name="inspection_report"
                                                value={formik.values.inspection_report}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                                style={{ textAlign: 'left' }}
                                                step="0.01"
                                                min="0"
                                                invalid={formik.touched.inspection_report && !!formik.errors.inspection_report}
                                            />
                                            {formik.touched.inspection_report && formik.errors.inspection_report && (
                                                <div className="text-danger small mt-1">{formik.errors.inspection_report}</div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Conditional: Deed Recording OR Rental Agreement */}
                                    {earnestDepositData?.agreement?.agreement_type === 'rental_agreement' ? (
                                        <tr style={{ border: 'none' }}>
                                            <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                                <span style={{ fontSize: '15px', color: '#495057' }}>Rental Agreement</span>
                                            </td>
                                            <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                                <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                    ${formatPrice(globalEarnestValues?.rental_agreement || 0)}
                                                </span>
                                            </td>
                                            <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                                <Input
                                                    type="number"
                                                    name="rentalAgreementPercentage"
                                                    value={formik.values.rentalAgreementPercentage}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="0.00"
                                                    style={{ textAlign: 'left' }}
                                                    step="0.01"
                                                    min="0"
                                                    invalid={formik.touched.rentalAgreementPercentage && !!formik.errors.rentalAgreementPercentage}
                                                />
                                                {formik.touched.rentalAgreementPercentage && formik.errors.rentalAgreementPercentage && (
                                                    <div className="text-danger small mt-1">{formik.errors.rentalAgreementPercentage}</div>
                                                )}
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr style={{ border: 'none' }}>
                                            <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                                <span style={{ fontSize: '15px', color: '#495057' }}>Deed Recording</span>
                                            </td>
                                            <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                                <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                    {globalEarnestValues?.deed_recording_percentage || 0}%
                                                </span>
                                            </td>
                                            <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                                <Input
                                                    type="number"
                                                    name="deed_recording_percentage"
                                                    value={formik.values.deed_recording_percentage}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    placeholder="0.00"
                                                    style={{ textAlign: 'left' }}
                                                    step="0.01"
                                                    min="0"
                                                    max="100"
                                                    invalid={formik.touched.deed_recording_percentage && !!formik.errors.deed_recording_percentage}
                                                />
                                                {formik.touched.deed_recording_percentage && formik.errors.deed_recording_percentage && (
                                                    <div className="text-danger small mt-1">{formik.errors.deed_recording_percentage}</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}

                                    {/* Tax Percentage */}
                                    <tr style={{ border: 'none' }}>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#495057' }}>Tax</span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                {globalEarnestValues?.tax_percentage || 0}%
                                            </span>
                                        </td>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <Input
                                                type="number"
                                                name="tax_percentage"
                                                value={formik.values.tax_percentage}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                                style={{ textAlign: 'left' }}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                invalid={formik.touched.tax_percentage && !!formik.errors.tax_percentage}
                                            />
                                            {formik.touched.tax_percentage && formik.errors.tax_percentage && (
                                                <div className="text-danger small mt-1">{formik.errors.tax_percentage}</div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Identity Verification Percentage */}
                                    <tr style={{ border: 'none' }}>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#495057' }}>Identity Verification</span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                {globalEarnestValues?.identity_verification_percentage || 0}%
                                            </span>
                                        </td>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <Input
                                                type="number"
                                                name="identity_verification_percentage"
                                                value={formik.values.identity_verification_percentage}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                                style={{ textAlign: 'left' }}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                invalid={formik.touched.identity_verification_percentage && !!formik.errors.identity_verification_percentage}
                                            />
                                            {formik.touched.identity_verification_percentage && formik.errors.identity_verification_percentage && (
                                                <div className="text-danger small mt-1">{formik.errors.identity_verification_percentage}</div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Transport & Logistics Percentage */}
                                    <tr style={{ border: 'none' }}>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#495057' }}>Transport & Logistics</span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                {globalEarnestValues?.transport_logistics_percentage || 0}%
                                            </span>
                                        </td>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <Input
                                                type="number"
                                                name="transport_logistics_percentage"
                                                value={formik.values.transport_logistics_percentage}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                                style={{ textAlign: 'left' }}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                invalid={formik.touched.transport_logistics_percentage && !!formik.errors.transport_logistics_percentage}
                                            />
                                            {formik.touched.transport_logistics_percentage && formik.errors.transport_logistics_percentage && (
                                                <div className="text-danger small mt-1">{formik.errors.transport_logistics_percentage}</div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Platform Fee Percentage */}
                                    <tr style={{ border: 'none' }}>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#495057' }}>Platform Fee</span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <span style={{ fontSize: '15px', color: '#6c757d' }}>
                                                {globalEarnestValues?.platform_fee_percentage || 0}%
                                            </span>
                                        </td>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', border: 'none' }}>
                                            <Input
                                                type="number"
                                                name="platform_fee_percentage"
                                                value={formik.values.platform_fee_percentage}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                placeholder="0.00"
                                                style={{ textAlign: 'left' }}
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                invalid={formik.touched.platform_fee_percentage && !!formik.errors.platform_fee_percentage}
                                            />
                                            {formik.touched.platform_fee_percentage && formik.errors.platform_fee_percentage && (
                                                <div className="text-danger small mt-1">{formik.errors.platform_fee_percentage}</div>
                                            )}
                                        </td>
                                    </tr>

                                    {/* Total Row */}
                                    <tr className="table-warning" style={{ borderTop: '2px solid #dee2e6' }}>
                                        <td className="py-3" style={{ verticalAlign: 'middle', borderRight: 'none', borderLeft: 'none', borderBottom: 'none' }}>
                                            <span style={{ fontSize: '16px', fontWeight: 600, color: '#212529' }}>Total</span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', borderLeft: 'none', borderBottom: 'none' }}>
                                            <span style={{ fontSize: '16px', fontWeight: 600, color: '#212529' }}>
                                                {globalEarnestValues?.total_percentage || 0}%
                                            </span>
                                        </td>
                                        <td className="py-3 text-center" style={{ verticalAlign: 'middle', borderRight: 'none', borderLeft: 'none', borderBottom: 'none' }}>
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: '16px',
                                                    color: '#212529'
                                                }}
                                            >
                                                {earnestDepositData?.total_percentage}%
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </form>
                    </>
                )}
            </ModalBody>
            <ModalFooter className="d-flex justify-content-end gap-2">
                <Button
                    color="danger"
                    onClick={handleClear}
                    disabled={updateMutation.isPending || isLoadingGlobal || !hasFormChanged()}
                    style={{ minWidth: '80px' }}
                >
                    Clear
                </Button>
                <Button
                    color="success"
                    onClick={() => formik.handleSubmit()}
                    disabled={updateMutation.isPending || isLoadingGlobal || !formik.isValid}
                    style={{ minWidth: '80px' }}
                >
                    {updateMutation.isPending ? 'Updating...' : 'Calculate & Save'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default FinalDisclosureModal;